import { useNavigate } from "react-router-dom";
import useTranslator from "../../hooks/useTranslator";
import { Button } from "../design";
import styles from "./SelfManagedGuarantorsModal.module.css";

export type SelfManagedGuarantor = {
  name: string;
  email?: string;
  relation?: string;
  gurantor_relation?: string;
  completed?: boolean;
  type?: string;
};

type Props = {
  guarantors: SelfManagedGuarantor[];
  onLater: () => void;
  inline?: boolean;
};

const SendIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="18"
    height="18"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
  >
    <path d="M22 2L11 13" />
    <path d="M22 2L15 22L11 13L2 9L22 2Z" />
  </svg>
);
const CardIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="18"
    height="18"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
  >
    <rect x="2" y="5" width="20" height="14" rx="2" />
    <path d="M2 9h20" />
    <path d="M7 15h4" />
  </svg>
);
const BriefcaseIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="18"
    height="18"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
  >
    <rect x="3" y="7" width="18" height="13" rx="2" />
    <path d="M8 7V5a4 4 0 0 1 8 0v2" />
    <path d="M3 12h18" />
  </svg>
);
const InfoIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="18"
    height="18"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 10v6" />
    <path d="M12 7h.01" />
  </svg>
);

export default function SelfManagedGuarantorsModal({
  guarantors,
  onLater,
  inline,
}: Props) {
  console.log("SelfManagedGuarantorsModal guarantors", guarantors);
  const navigate = useNavigate();
  const { _t } = useTranslator();
  const content = (
    <div className={styles.card} onClick={(e) => e.stopPropagation()}>
      <h2>{_t("onboarding_self_managed_title", "Create your application")}</h2>
      <p className={styles.subtitle}>
        {_t(
          "onboarding_self_managed_subtitle",
          "Complete these profiles yourself."
        )}
      </p>
      <div className={styles.whatNext}>
        <strong>{_t("onboarding_what_next_title", "What happens next")}</strong>
        <div className={styles.whatRow}>
          <span className={styles.iconWrap}>
            <SendIcon />
          </span>
          <div className={styles.whatText}>
            <b>
              {_t("onboarding_what_next_invited_title", "Invited profiles")}
            </b>
            <p>
              {_t(
                "onboarding_what_next_invited_desc",
                "We'll email them automatically. They complete their own application."
              )}
            </p>
          </div>
        </div>
        <div className={styles.whatRow}>
          <span className={styles.iconWrap}>
            <CardIcon />
          </span>
          <div className={styles.whatText}>
            <b>
              {_t(
                "onboarding_what_next_self_managed_title",
                "Profiles you'll fill in"
              )}
            </b>
            <p className={styles.needFor}>
              {_t(
                "onboarding_what_next_self_managed_need",
                "You'll need, for each person:"
              )}
            </p>
            <div className={styles.needRow}>
              <CardIcon />
              <span>
                {_t("onboarding_what_next_need_id", "Their ID or passport")}
              </span>
            </div>
            <div className={styles.needRow}>
              <BriefcaseIcon />
              <span>
                {_t(
                  "onboarding_what_next_need_income",
                  "Proof of work and income (payslips, contract or invoices)"
                )}
              </span>
            </div>
            <div className={styles.infoRow}>
              <InfoIcon />
              <span>
                {_t(
                  "onboarding_what_next_consent_info",
                  "When you complete a profile, we'll email that person to confirm you had their permission, review their details, and accept the terms before we process their information."
                )}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className={styles.list}>
        {guarantors.map((g) => {
          const [firstName = "", ...lastNameParts] = g.name.trim().split(/\s+/);
          const lastName = lastNameParts.join(" ");
          return (
            <div key={g.email || g.name} className={styles.row}>
              <span className={styles.avatar}>
                {(g.name || "G").charAt(0).toUpperCase()}
              </span>
              <div className={styles.meta}>
                <strong>{g.name}</strong>
                <span>
                  {(g as any).type === "co_tenant"
                    ? _t("co_tenant", "Co-tenant")
                    : _t("guarantor", "Guarantor")}
                </span>
              </div>
              {g.completed ? (
                <span className={styles.completed}>
                  ✓ {_t("onboarding_self_managed_completed", "Completed")}
                </span>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const pendingCount = guarantors.filter(
                      (x) => !x.completed
                    ).length;
                    const isCoTenant = (g as any).type === "co_tenant";
                    const params = new URLSearchParams(
                      isCoTenant
                        ? { coTenantName: g.name }
                        : { guarantorName: g.name }
                    );
                    if (g.email) {
                      params.set("email", g.email);
                    }
                    if (!isCoTenant) {
                      params.set("firstName", firstName);
                      if (lastName) params.set("lastName", lastName);
                    }
                    const relation = g.relation || g.gurantor_relation;
                    if (!isCoTenant && relation) {
                      params.set("gurantor_relation", relation);
                    }
                    const filteredForStorage = guarantors.filter(
                      (x: any) =>
                        (x.type || "guarantor") ===
                        (isCoTenant ? "co_tenant" : "guarantor")
                    );
                    if (
                      filteredForStorage.filter((x: any) => !x.completed)
                        .length > 1
                    ) {
                      params.set("returnTo", "/onboarding?welcome=true");
                    }
                    params.set("showSidebar", "true");
                    try {
                      sessionStorage.setItem(
                        isCoTenant
                          ? "selfManagedCoTenants"
                          : "selfManagedGuarantors",
                        JSON.stringify(filteredForStorage)
                      );
                    } catch {}
                    // Use window navigation to avoid being intercepted by parent modal/route guards
                    window.location.assign(
                      `${isCoTenant ? "/co-tenant/onboarding" : "/guarantor/onboarding"}?${params.toString()}`
                    );
                  }}
                >
                  {_t("onboarding_self_managed_start", "Start")}
                </Button>
              )}
            </div>
          );
        })}
      </div>
      <Button className={styles.later} variant="link" onClick={onLater}>
        {_t(
          "onboarding_self_managed_later",
          "Run them later from your dashboard"
        )}
      </Button>
    </div>
  );
  if (inline) return content;
  return (
    <div className={styles.overlay} onClick={onLater}>
      {content}
    </div>
  );
}
